import { Divider, Link, TextField, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { FunctionComponent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useLocation } from 'react-router-dom';
import Backend, { OrgInfo, SaveCredsResponse, UserInfo } from '../../backend/Backend';
import SFDCMode from '../SFDCMode';

import { useOktaAuth } from '@okta/okta-react';
import DataSourceType from '../../DataSourceType';
import GoalType from '../../GoalType';
import SFDCAppScope from '../SFDCAppScope';
import SFDCSourceFileType from '../SFDCSourceFileType';
import {
    appendPkceToUrl,
    consumePkceVerifierForNonce,
    generateCodeChallenge,
    generateCodeVerifier,
    PKCE_SESSION_VERIFIER_SOURCE,
    PKCE_SESSION_VERIFIER_TARGET,
    sfdcOAuthCallbackUrl,
} from '../pkce';
import './SFDC.scss';

const auth_code_url =
    'https://{0}/services/oauth2/authorize?client_id={1}&redirect_uri={2}' +
    '&response_type=code&display=popup&prompt=login+consent&state={3}';

const access_token_url =
    'https://{0}/services/oauth2/authorize?client_id={1}&redirect_uri={2}' +
    '&response_type=token&display=popup&prompt=login+consent&state={3}';

let report_in_process = false;

interface SFDCProps extends RouteComponentProps {
    mode: SFDCMode;
    appScope?: SFDCAppScope;
    progress: Function;
    header?: Function;
    debug?: Boolean;
    onCredentials?: (creads: SaveCredsResponse) => void;
    goalType?: GoalType;
    selectedSourceType?: DataSourceType;
}

enum OrgType {
    production = 'login.salesforce.com',
    sandbox = 'test.salesforce.com',
    custom = 'custom',
}

const SFDC: FunctionComponent<SFDCProps> = (props: SFDCProps) => {
    const [orgType, setOrgType] = useState(OrgType.production);
    const [source_auth_code, setSourceAuthCode] = useState('');
    const [source_domain, setSourceDomain] = useState('');
    const [source_access_token, setSourceAccessToken] = useState('');
    const [source_refresh_token, setSourceRefreshToken] = useState('');
    const [target_auth_code, setTargetAuthCode] = useState('');
    const [target_domain, setTargetDomain] = useState('');
    const [source_user_info, setSourceUserInfo] = useState<UserInfo | null>(null);
    const [source_org_info, setSourceOrgInfo] = useState<OrgInfo | null>(null);
    const [console_log, setConsoleLog] = useState('');
    const [processor_type, setProcessorType] = useState('');
    const [files, setFilesToUpload] = useState(null);
    const [files_source_type, setFilesSourceType] = useState(SFDCSourceFileType.svmx);
    const { authState, oktaAuth } = useOktaAuth();
    const [deploy_report_task_id, setDeployReportTaskId] = useState('');
    const sourcePkceVerifierRef = useRef<string | undefined>(undefined);
    const targetPkceVerifierRef = useRef<string | undefined>(undefined);

    const { search, hash } = useLocation();
    const search_params = useMemo(() => new URLSearchParams(hash || search), [search]);
    const appScope = props.appScope || search_params.get('appScope') || SFDCAppScope.default;

    const { t } = useTranslation();

    function log(str: string) {
        str = str || '';
        setConsoleLog((prev_log_str: string) => `${prev_log_str + str}\n`);
    }

    function selectSource() {
        const win = popupWindow(`/sfdc/source?appScope=${appScope}`, 'Service Insights - Source');
        (window as any).setLoginResponse = function (
            auth_code: string,
            domain: string,
            instance_url?: string,
            code_verifier?: string,
        ) {
            setSourceAuthCode(auth_code);
            setSourceDomain(domain);
            sourcePkceVerifierRef.current = code_verifier;
            log(`Source Auth Code: ${auth_code}`);

            win!.close();
            begin(auth_code, domain, instance_url);
        };
    }

    function selectTarget() {
        const win = popupWindow('/sfdc/target', 'Service Insights - Target');
        (window as any).setLoginResponse = function (
            auth_code: string,
            domain: string,
            _instance_url?: string,
            code_verifier?: string,
        ) {
            setTargetAuthCode(auth_code);
            setTargetDomain(domain);
            targetPkceVerifierRef.current = code_verifier;
            win!.close();
        };
    }

    function login() {
        if (appScope === SFDCAppScope.default) {
            loginDefault();
        } else if (appScope === SFDCAppScope.report) {
            loginReport();
        } else {
            alert('Invalid app scope. This should never happen!');
        }
    }

    function loginReport() {
        const loginUrl = access_token_url
            .replace('{0}', orgType)
            .replace('{1}', getAuthClientId())
            .replace('{2}', getCallbackUrl(props.mode))
            .replace('{3}', `${orgType},${appScope}`);

        window.location.href = loginUrl;
    }

    async function loginDefault() {
        const sessionKey =
            props.mode === SFDCMode.target ? PKCE_SESSION_VERIFIER_TARGET : PKCE_SESSION_VERIFIER_SOURCE;
        const verifier = generateCodeVerifier();
        const challenge = await generateCodeChallenge(verifier);
        sessionStorage.setItem(sessionKey, verifier);

        let loginUrl = auth_code_url
            .replace('{0}', orgType)
            .replace('{1}', getAuthClientId())
            .replace('{2}', getCallbackUrl(props.mode))
            .replace('{3}', `${orgType},${appScope}`);

        loginUrl = appendPkceToUrl(loginUrl, challenge);
        window.location.href = loginUrl;
    }

    async function begin(sac?: string, sd?: string, iu?: string) {
        if (appScope === SFDCAppScope.default) {
            beginDefault(sac, sd);
        } else if (appScope === SFDCAppScope.report) {
            beginReport(sac, iu);
        } else {
            alert('Invalid app scope. This should never happen!');
        }
    }

    async function beginReport(sac?: string, iu?: string) {
        if (report_in_process === true) {
            return;
        }

        report_in_process = true;
        const response = await deployReport(sac, iu);
        setDeployReportTaskId(response.data.task_id);

        checkDeployReportStatus();
    }

    async function deployReport(sac?: string, iu?: string) {
        props.progress(true);
        const resp = await Backend.deployReport(sac!, iu!, props.selectedSourceType!, props.goalType!, oktaAuth);
        props.progress(false);
        return resp;
    }

    async function checkDeployReportStatus() {
        report_in_process = false;
    }

    async function beginDefault(sac?: string, sd?: string) {
        props.progress(true);
        const effectiveSource = sac ? sac : source_auth_code;
        const oauthRedirectUri = effectiveSource
            ? sfdcOAuthCallbackUrl('source')
            : sfdcOAuthCallbackUrl('target');
        const resp = await Backend.saveCreds(
            sac ? sac : source_auth_code,
            sd ? sd : source_domain,
            target_auth_code,
            target_domain,
            oktaAuth,
            sourcePkceVerifierRef.current,
            targetPkceVerifierRef.current,
            oauthRedirectUri,
        );
        sourcePkceVerifierRef.current = undefined;
        targetPkceVerifierRef.current = undefined;

        setSourceAccessToken(resp.data.source_user_info.access_token);
        setSourceRefreshToken(resp.data.source_user_info.refresh_token);
        setSourceUserInfo(resp.data.source_user_info);
        setSourceOrgInfo(resp.data.source_org_info);

        log(`Source Access Token: ${resp.data.source_user_info.access_token}`);
        log(`Source Refresh Token: ${resp.data.source_user_info.refresh_token}`);
        log(`User Name: ${resp.data.source_user_info.user_name}`);
        log(`User E-mail:${resp.data.source_user_info.user_email}`);
        log(`Is Salesforce Field Service Org: ${resp.data.source_org_info.is_fsl}`);
        log(`Is ServiceMax Org: ${resp.data.source_org_info.is_svmx}`);
        log(`Is Custom Org: ${resp.data.source_org_info.is_custom}`);
        props.progress(false);

        if (props.onCredentials) {
            props.onCredentials(resp.data);
        }
    }

    function getCallbackUrl(mode: SFDCMode) {
        const callbackUrl = `${window.location.origin}/sfdc/${mode}/oauth2/callback`;

        return callbackUrl;
    }

    function getAuthClientId() {
        if (appScope === SFDCAppScope.default) {
            return window.REACT_APP_CONFIG?.REACT_APP_SFDC_APP_CLIENT_ID as string || process.env.REACT_APP_SFDC_APP_CLIENT_ID;
        }
        if (appScope === SFDCAppScope.report) {
            return window.REACT_APP_CONFIG?.REACT_APP_SFDC_APP_CLIENT_ID_REPORT_SCOPE as string || process.env.REACT_APP_SFDC_APP_CLIENT_ID_REPORT_SCOPE;
        }
        alert('Invalid app scope. This should never happen!');

        return '';
    }

    async function processSource() {
        if (files !== null) {
            const res = await processFileUpload();
            return res;
        }
        const res = await processSourceInternal();

        return res;
    }

    async function processFileUpload() {
        props.progress(true);
        let resp = null;
        if (files_source_type === SFDCSourceFileType.svmx) {
            resp = await Backend.uploadSvmx(files);
            console.log(resp);
        } else {
            // TODO:
        }

        const filesInfo = resp!.data.files;
        for (let i = 0; i < filesInfo.length; i++) {
            const f = filesInfo[i];
            log(`File name: ${f.file_name}`);
            const mapping = f.field_mapping;
            log('--------------------------------');
            log(`${pad('Template Column') + pad('CSV Column')}Similarity Score`);
            log(`${pad('-') + pad('-')}-`);
            for (let j = 0; j < mapping.length; j++) {
                const m = mapping[j];
                log(pad(m.target) + pad(m.source) + m.score);
            }

            logReport(f.report);
        }

        log('Upload process complete');
        props.progress(false);
    }

    async function processSourceInternal() {
        props.progress(true);
        const resp = await Backend.process(
            source_access_token,
            source_user_info?.instance_url!,
            source_org_info!,
            oktaAuth,
        );

        setProcessorType(resp.data.message);
        log(`Processor Type: ${resp.data.message}`);
        log(`First Level Work Order Count: ${resp.data.wo_count}`);
        log(`Second Level Work Order Count: ${resp.data.second_level_wo_count}`);
        log(`Case Count: ${resp.data.case_count}`);
        log(`Template Id: ${resp.data.template_id}`);

        const reports = resp.data.report;
        for (let i = 0; i < reports.length; i++) {
            const r = reports[i];
            logReport(r);
        }

        log('Source process complete');
        props.progress(false);
    }

    function logReport(r: any) {
        log('--------------------------------');
        log(`Report name: ${r.name}`);
        log(`Total lines: ${r.total_lines}`);
        log(
            `Data expected vs. contained: ${(((r.total_expected_values - r.total_missing_values) / r.total_expected_values) * 100).toFixed(2)}%`,
        );
        log('');

        r.columns.sort((a: any, b: any) => (a.total_missing_values > b.total_missing_values ? -1 : 1));
        for (let j = 0; j < r.columns.length; j++) {
            const c = r.columns[j];
            log(`${pad(c.column_name)}${c.total_missing_values}`);
        }
        log('--------------------------------');
    }

    function pad(s: string) {
        const padding = 60 - s.length;
        s = s + new Array(padding).join(' ');
        return s;
    }

    function handleSvmxFileUpload(event: any) {
        const { files } = event.target;
        setFilesToUpload(files);
        setFilesSourceType(SFDCSourceFileType.svmx);
    }

    async function handleReportCsvUpload(event: any) {
        const { files } = event.target;
        setFilesToUpload(files);
        doReportCsvUpload(files);
    }

    async function doReportCsvUpload(files: any) {
        props.progress(true);
        const resp = await Backend.doReportCsvUpload(files, deploy_report_task_id, oktaAuth);
        props.progress(false);
    }

    function popupWindow(url: string, windowName: string) {
        url = window.location.origin + url;

        // TODO: adjust for all resolutions...
        const w = 600;
        const h = 500;
        const y = window.top!.outerHeight / 2 + window.top!.screenY - h / 2;
        const x = window.top!.outerWidth / 2 + window.top!.screenX - w / 2;
        return window.open(
            url,
            windowName,
            `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, 
      resizable=no, copyhistory=no, width=${w}, height=${h}, top=${y}, left=${x}`,
        );
    }

    switch (props.mode) {
        case SFDCMode.home: {
            return (
                <Stack style={{ width: '100%' }} spacing={2} direction="column" sx={{ margin: 1 }}>
                    <Stack spacing={2} direction="row" alignItems="center" justifyContent="center">
                        <Button variant="contained" onClick={() => selectSource()}>
                            {t('import.login')}
                        </Button>
                        {appScope === SFDCAppScope.report && (
                            <label htmlFor="contained-button-file-report-csv">
                                <input
                                    accept="text/csv"
                                    id="contained-button-file-report-csv"
                                    type="file"
                                    style={{ display: 'none' }}
                                    onChange={(event) => handleReportCsvUpload(event)}
                                />
                                <Button variant="contained" component="span">
                                    {' '}
                                    {t('import.select-file-and-upload')}
                                </Button>
                            </label>
                        )}
                        {false ? (
                            <Button variant="contained" onClick={() => selectTarget()}>
                                Select Target
                            </Button>
                        ) : null}
                    </Stack>

                    {props.debug ? (
                        <div>
                            <Typography classes={{ h6: 'Or-Label' }} variant="h6" component="h6">
                                -OR-
                            </Typography>
                            <Stack spacing={2} direction="row" sx={{ margin: 1 }}>
                                <label htmlFor="contained-button-file-svmx">
                                    <input
                                        accept="text/csv"
                                        id="contained-button-file-svmx"
                                        type="file"
                                        style={{ display: 'none' }}
                                        onChange={(event) => handleSvmxFileUpload(event)}
                                    />
                                    <Button variant="contained" component="span">
                                        {' '}
                                        {'Select & Upload ServiceMax CSV'}
                                    </Button>
                                </label>
                                <Link>Instructions to download service data</Link>
                            </Stack>
                            <Typography classes={{ h6: 'Or-Label' }} variant="h6" component="h6">
                                -OR-
                            </Typography>
                            <Stack spacing={2} direction="row" sx={{ margin: 1 }}>
                                <label htmlFor="contained-button-file-fsl">
                                    <input
                                        accept="text/csv"
                                        id="contained-button-file-fsl"
                                        type="file"
                                        style={{ display: 'none' }}
                                    />
                                    <Button variant="contained" component="span">
                                        {' '}
                                        {'Select & Upload FSL CSV'}
                                    </Button>
                                </label>
                                <Link>Instructions to download service data</Link>
                            </Stack>
                            <Divider />
                            <Stack spacing={2} direction="row">
                                <Button color="secondary" size="small" onClick={() => processSource()}>
                                    Process Source
                                </Button>
                            </Stack>{' '}
                        </div>
                    ) : null}

                    <Divider />
                    <FormControl fullWidth>
                        <TextField
                            InputProps={{ classes: { input: 'ConsoleLog' } }}
                            multiline
                            label="Console Log"
                            rows={15}
                            value={console_log}
                        />
                    </FormControl>
                    <Stack spacing={2} direction="row">
                        <Button variant="contained" onClick={() => setConsoleLog('')}>
                            Clear Log
                        </Button>
                    </Stack>
                </Stack>
            );
        }

        case SFDCMode.source:
        case SFDCMode.target: {
            props.header!(false);
            return (
                <div className="SFDC">
                    <Stack spacing={2} direction="column">
                        <FormControl fullWidth>
                            <InputLabel id="org_type_label">Select Org Type</InputLabel>
                            <Select
                                label="org_type_label"
                                value={orgType}
                                onChange={(event) => {
                                    const ot = event.target.value as OrgType;
                                    setOrgType(ot);
                                }}
                            >
                                <MenuItem value={OrgType.production}>Production</MenuItem>
                                <MenuItem value={OrgType.sandbox}>Sandbox</MenuItem>
                                <MenuItem value={OrgType.custom}>Custom</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl>
                            <Button variant="contained" onClick={() => login()}>
                                Login
                            </Button>
                        </FormControl>
                    </Stack>
                </div>
            );
        }

        case SFDCMode.source_callback:
        case SFDCMode.target_callback: {
            props.header!(false);
            try {
                const st = search_params.get('state');
                const stateParts = st?.split(',') ?? [];
                const ot = stateParts[0];
                const scope = stateParts[1];
                const pkceNonce = stateParts[2];

                const oauthError = search_params.get('error');
                if (oauthError) {
                    const raw = search_params.get('error_description') ?? '';
                    let description = raw;
                    try {
                        description = raw ? decodeURIComponent(raw.replace(/\+/g, ' ')) : '';
                    } catch {
                        /* keep raw */
                    }
                    throw new Error(
                        `Salesforce OAuth error: ${oauthError}${description ? ` — ${description}` : ''}`,
                    );
                }

                if (!st || stateParts.length < 2 || !ot || !scope) {
                    throw new Error(
                        `Invalid state (expected org,scope[,pkceNonce]): ${JSON.stringify({ st, stateParts })}`,
                    );
                }

                if (scope !== SFDCAppScope.default && scope !== SFDCAppScope.report) {
                    throw new Error(`Unknown app scope in state: ${scope}`);
                }

                let code: string | null = null;
                let iu: string | null = null;
                if (scope === SFDCAppScope.report) {
                    code = search_params.get('#access_token');
                    iu = search_params.get('instance_url');
                } else {
                    code = search_params.get('code');
                }

                if (scope === SFDCAppScope.default && !code) {
                    throw new Error('Missing authorization code');
                }

                let codeVerifier: string | undefined;
                if (pkceNonce) {
                    codeVerifier = consumePkceVerifierForNonce(pkceNonce) ?? undefined;
                } else {
                    const sessionKey =
                        props.mode === SFDCMode.source_callback
                            ? PKCE_SESSION_VERIFIER_SOURCE
                            : PKCE_SESSION_VERIFIER_TARGET;
                    const stored = sessionStorage.getItem(sessionKey);
                    sessionStorage.removeItem(sessionKey);
                    codeVerifier = stored ?? undefined;
                }

                (window.opener as any).setLoginResponse(code, ot, iu, codeVerifier);
                return <div>Auth Code: {code}</div>;
            } catch (err) {
                console.error('[SFDC OAuth callback]', err);
                return <div />;
            }
        }
        default: {
            return <div>SFDC Home</div>;
        }
    }
};

export default SFDC;
